import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/language/LanguageContext';
import CountUp from '../behavior/CountUp';
import { Button } from '@/components/ui/button';

export const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const stats = [
    { number: '2+', label: t('about.stats.years') },
    { number: '10+', label: t('about.stats.projects') },
    { number: '5+', label: t('about.stats.clients') },
  ];

  const skills = t('about.skills');

  return (
    <section
      id="about"
      ref={sectionRef}
      className="min-h-screen bg-background py-24 lg:py-32 px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div
          className={`mb-16 lg:mb-24 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-muted-foreground text-sm font-light tracking-[0.2em] uppercase mb-4">
            {t('about.sectionTitle')}
          </p>
          <div className="w-12 h-px bg-border" />
        </div>

        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left column - Main content */}
          <div
            className={`transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-foreground text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-tight mb-8">
              {t('about.heading1')}
              <br />
              <span className="text-muted-foreground">{t('about.heading2')}</span>
            </h2>

            <div className="space-y-6 text-muted-foreground font-light leading-relaxed">
              <p>{t('about.bio1')}</p>
              <p>{t('about.bio2')}</p>
              <p>{t('about.bio3')}</p>
            </div>

            {/* Skills */}
            <div className="mt-12">
              <p className="text-muted-foreground text-xs font-light tracking-[0.2em] uppercase mb-6">
                {t('about.expertise')}
              </p>
              <div className="flex flex-wrap gap-3">
                {Array.isArray(skills) && skills.map((skill) => (
                  <Button
                    key={skill}
                    variant="outline"
                    className="px-4 py-2 border border-border text-foreground text-sm font-light tracking-wide hover:bg-white hover:text-black transition-colors"
                  >
                    {skill}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Right column - Stats */}
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="lg:pl-12 lg:border-l border-border">
              <div className="space-y-12">
                {stats.map((stat, index) => (
                  <div
                    key={stat.label}
                    className={`transition-all duration-500`}
                    style={{ transitionDelay: `${(index + 2) * 100}ms` }}
                  >
                    <p className="text-foreground text-5xl lg:text-6xl font-light tracking-tight mb-2">
                    <CountUp
                      to={stat.number}
                      duration={1}
                    />+
                    </p>
                    <p className="text-muted-foreground text-sm font-light tracking-wide">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>

              {/* Quote */}
              <blockquote className="mt-16 pt-8 border-t border-border">
                <p className="text-muted-foreground text-lg font-light italic leading-relaxed">
                  {t('about.quote')}
                </p>
                <cite className="text-foreground/60 text-sm font-light mt-4 block not-italic">
                  {t('about.quoteAuthor')}
                </cite>
              </blockquote>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
